import { createApi } from '@reduxjs/toolkit/query/react';
import { customBaseQuery } from '@/lib/baseQuery';

import { ApiResponse, User } from '@/shared/types';
import {
  UserDetailDto,
  UserDto,
  UserRolesMutationArg,
  SetCaLevelArg,
} from '@/pages/access-control/routes/users/types/users.dto';

export type ProfilePayload = {
  name: string;
  phone: string;
  designationId: number;
};
export const userApi = createApi({
  reducerPath: 'userApi',
  baseQuery: customBaseQuery,
  tagTypes: ['Users'],
  endpoints: (builder) => ({
    getUsers: builder.query<ApiResponse<UserDto[]>, { roles?: string } | void>({
      query: (args) => ({
        url: '/v1/users',
        method: 'GET',
        params: {
          ...(args?.roles && { roles: args.roles }),
        },
      }),
      providesTags: (result) =>
        result?.data
          ? [
              { type: 'Users' as const, id: 'LIST' },
              ...result.data.map((u) => ({ type: 'Users' as const, id: u.id })),
            ]
          : [{ type: 'Users' as const, id: 'LIST' }],
    }),
    updateProfile: builder.mutation<ApiResponse<User>, Partial<ProfilePayload>>(
      {
        query: (body) => ({
          url: '/v1/users/me/profile',
          method: 'PUT',
          body,
        }),
        invalidatesTags: [{ type: 'Users', id: 'LIST' }],
      },
    ),
    getUserById: builder.query<ApiResponse<UserDetailDto>, number>({
      query: (id) => ({
        url: `/v1/users/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'Users', id }],
    }),
    assignRoles: builder.mutation<
      ApiResponse<UserDetailDto>,
      UserRolesMutationArg
    >({
      query: ({ id, roleIds }) => ({
        url: `/v1/users/${id}/roles`,
        method: 'POST',
        body: { roleIds },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Users', id },
        { type: 'Users', id: 'LIST' },
      ],
      // Optional: keep UI snappy even before refetch finishes
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            userApi.util.updateQueryData('getUserById', id, (draft) => {
              draft.data.roles = data.data.roles;
            }),
          );
        } catch {
          // ignore
        }
      },
    }),

    setCaLevel: builder.mutation<ApiResponse<UserDto>, SetCaLevelArg>({
      query: ({ id, caLevel }) => ({
        url: `/v1/users/${id}/ca-level`,
        method: 'PATCH',
        body: { caLevel },
      }),
      // No tag invalidation: getUsers provides a tag per user, so any
      // invalidation refetches the list, and GET /v1/users is cached
      // server-side for a few seconds (it could return the old level and
      // overwrite the patch). Patch the caches from the PATCH response instead.
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const level = data.data.caLevel;
          if (level === undefined) return;
          dispatch(
            userApi.util.updateQueryData('getUsers', undefined, (draft) => {
              const u = draft.data.find((x) => Number(x.id) === Number(id));
              if (u) u.caLevel = level;
            }),
          );
          dispatch(
            userApi.util.updateQueryData('getUserById', Number(id), (draft) => {
              draft.data.caLevel = level;
            }),
          );
        } catch {
          // ignore
        }
      },
    }),

    replaceRoles: builder.mutation<
      ApiResponse<UserDetailDto>,
      UserRolesMutationArg
    >({
      query: ({ id, roleIds }) => ({
        url: `/v1/users/${id}/roles`,
        method: 'PUT',
        body: { roleIds },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Users', id },
        { type: 'Users', id: 'LIST' },
      ],
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            userApi.util.updateQueryData('getUserById', id, (draft) => {
              draft.data.roles = data.data.roles;
            }),
          );
        } catch {
          // ignore
        }
      },
    }),
  }),
});
export const {
  useGetUsersQuery,
  useGetUserByIdQuery,
  useUpdateProfileMutation,
  useAssignRolesMutation,
  useReplaceRolesMutation,
  useSetCaLevelMutation,
} = userApi;
